import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { requiredValidation } from 'validators';
import { ConfirmationModal, TextField, Typography, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import { useStoreActions } from '~/contexts';
import { useIntegrations } from '~/hooks/reactQuery/queries/useIntegrations';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import { getPMDetails } from '~/integrations/PracticeManagement/pm.utils';
import { PracticeManagement } from '~/types/api.model';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import styles from './ConnectKeeperModal.module.scss';
export const GetConnectKeeperModalTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            ConnectKeeperModalTID: {
                Content: 'keeper-connect-modal-content',
                ClientId: 'keeper-client-id',
                ClientSecret: 'keeper-client-secret',
                ErrorToast: 'keeper-connect-error-toast'
            }
        }
    });
export default function ConnectKeeperModal(param) {
    let { dismissModal } = param;
    const { ConnectKeeperModalTID } = useMemo(()=>GetConnectKeeperModalTID(), []);
    const { t: availableAppsKeeperTexts } = useLocaleKeys('integrations.availableApps.keeper');
    const { t: keeperTexts } = useLocaleKeys('integrations.keeper');
    const { t: commonTexts } = useLocaleKeys('common');
    const { control, handleSubmit } = useForm({
        mode: 'all',
        defaultValues: {
            clientId: '',
            clientSecret: ''
        }
    });
    const { api } = useStoreActions();
    const { showErrorToast, startLoader, stopLoader } = useOverlayContext();
    const { refresh: refreshIntegrations } = useIntegrations({
        enabled: false
    });
    const keeperDetails = getPMDetails(PracticeManagement.Keeper);
    const submit = async (data)=>{
        startLoader();
        const { success } = await handleRequest(api.keeper.connect(data));
        if (!success) {
            showErrorToast(commonTexts.toasts.invalidCredentials(), ConnectKeeperModalTID.ErrorToast());
        } else {
            await refreshIntegrations({
                throwOnError: false
            });
            dismissModal();
        }
        stopLoader();
    };
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'connect_keeper_confirm'
            },
            secondaryButton: {
                name: 'connect_keeper_cancel'
            }
        },
        title: keeperTexts.connectTitle({
            name: keeperDetails.name
        }),
        width: "650px",
        content: /*#__PURE__*/ _jsxs("div", {
            className: styles.keeperModalContainer,
            "data-testid": ConnectKeeperModalTID.Content(),
            children: [
                /*#__PURE__*/ _jsxs(Typography, {
                    align: "left",
                    children: [
                        keeperTexts.connectDescription({
                            name: keeperDetails.name
                        }),
                        ' ',
                        /*#__PURE__*/ _jsx(ExternalLinkButton, {
                            openInNewTab: true,
                            href: keeperDetails.articleLink,
                            underline: "none",
                            bi: {
                                name: 'keeper_connect_learn_more'
                            },
                            children: keeperTexts.learnMore()
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Controller, {
                    name: "clientId",
                    control: control,
                    rules: requiredValidation('Please provide a Client ID'),
                    render: (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                            bi: {
                                name: 'keeper_client_id'
                            },
                            collapseHelperText: false,
                            testId: ConnectKeeperModalTID.ClientId(),
                            label: availableAppsKeeperTexts.clientId()
                        }, connectControllerToTextField(p)))
                }),
                /*#__PURE__*/ _jsx(Controller, {
                    name: "clientSecret",
                    control: control,
                    rules: requiredValidation('Please provide a Client Secret'),
                    render: (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                            bi: {
                                name: 'keeper_client_secret'
                            },
                            collapseHelperText: false,
                            testId: ConnectKeeperModalTID.ClientSecret(),
                            label: availableAppsKeeperTexts.clientSecret()
                        }, connectControllerToTextField(p)))
                })
            ]
        }),
        primaryButtonLabel: commonTexts.buttons.set(),
        secondaryButtonLabel: commonTexts.buttons.cancel(),
        onPrimaryButtonClick: handleSubmit(submit),
        onCloseButtonClick: dismissModal,
        onSecondaryButtonClick: dismissModal,
        testId: "connect-keeper-modal"
    });
}
